import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldAlert, Save } from "lucide-react";

const FIELDS = [
  { k: "safety_stop_pct", label: "Tanpon Prop Firm (%)", min: 50, max: 95, step: 5, pct: true, hint: "Kanpe nan X% chak limit prop firm." },
  { k: "consec_loss_per_pair", label: "Pèt konsekitif / pè", min: 1, max: 10, step: 1, hint: "Poz pè a apre N pèt youn dèyè lòt." },
  { k: "cross_pair_loss_count", label: "Pè an pèt (kwaze)", min: 1, max: 5, step: 1, hint: "Konbyen pè diferan an pèt anvan poz global." },
  { k: "cross_pair_loss_window_min", label: "Fenèt kwaze (min)", min: 5, max: 240, step: 5, hint: "Nan konbyen minit." },
  { k: "cross_pair_pause_min", label: "Poz bòt global (min)", min: 5, max: 1440, step: 5, hint: "Dire poz tout bòt la." },
  { k: "vol_circuit_atr_pct", label: "Disjonktè ATR (%)", min: 0, max: 10, step: 0.5, hint: "Poz sou volatilite ekstrèm (0 = etenn)." },
  { k: "vol_circuit_pause_min", label: "Poz volatilite (min)", min: 1, max: 240, step: 1, hint: "Konbyen tan pou tann." },
];

export function AdminProtectionDefaults() {
  const [vals, setVals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applyAll, setApplyAll] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/protection-defaults");
        const v = { ...data.values };
        v.safety_stop_pct = Math.round((Number(v.safety_stop_pct) || 0.8) * 100);
        setVals(v);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setVals((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const values = { ...vals, safety_stop_pct: Number(vals.safety_stop_pct) / 100 };
      const { data } = await api.post("/admin/protection-defaults", { values, apply_to_all: applyAll });
      toast.success(applyAll ? `Anrejistre + aplike sou ${data.applied_to_bots} kliyan` : "Default yo anrejistre");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !vals) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#1C8CFF]" /></div>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-protection-defaults">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-[#1C8CFF]" />
        <div>
          <div className="font-heading font-semibold text-white">Valè Pwoteksyon Default</div>
          <p className="text-xs text-[#888]">Sa vin default pou nouvo kliyan. Tcheke "Aplike sou tout" pou fòse l sou tout kliyan ki egziste kounye a.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <div key={f.k} className="border border-[#222] bg-[#0c0c0c] rounded-lg p-3">
            <div className="text-xs font-semibold text-white mb-1">{f.label}</div>
            <Input type="number" min={f.min} max={f.max} step={f.step} value={vals[f.k] ?? ""}
              onChange={(e) => set(f.k, e.target.value)} data-testid={`prot-default-${f.k}`}
              className="bg-black border-[#222] h-10 text-white" />
            <p className="text-[10px] text-[#666] mt-1">{f.hint}</p>
          </div>
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm text-white cursor-pointer" data-testid="apply-all-toggle">
        <input type="checkbox" checked={applyAll} onChange={(e) => setApplyAll(e.target.checked)} className="h-4 w-4" />
        Aplike sou TOUT kliyan ki egziste kounye a (fòse)
      </label>

      <Button onClick={save} disabled={saving} data-testid="save-protection-defaults"
        className="bg-[#1C8CFF] hover:bg-[#1677d6] text-white rounded-lg h-11 w-full">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Anrejistre Default yo</>}
      </Button>
    </div>
  );
}
